import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DEFAULT_LANGUAGE,
  Language,
  MessageKey,
  isLanguage,
  translate,
} from './catalogs';

export const LANGUAGE_STORAGE_KEY = 'mas3.language';

export interface I18nValue {
  language: Language;
  setLanguage(language: Language): void;
  t(key: MessageKey): string;
}

const I18nContext = createContext<I18nValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => undefined,
  t: (key) => translate(DEFAULT_LANGUAGE, key),
});

function readStoredLanguage(): Language {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_LANGUAGE;
  }
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isLanguage(stored) ? stored : DEFAULT_LANGUAGE;
}

export function I18nProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode;
  initialLanguage?: Language;
}): JSX.Element {
  const [language, setLanguageState] = useState<Language>(initialLanguage ?? readStoredLanguage());

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ language, setLanguage, t: (key) => translate(language, key) }),
    [language, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}
